import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileCode } from "lucide-react";
import { api } from "@/lib/api";

export const EaDownload = () => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ea-file-info");
      setInfo(data);
    } catch {
      setInfo({ available: false });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const grab = async (asFile) => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/ea-file", {
        params: { download: false },
        responseType: "text",
        transformResponse: [(d) => d],
      });
      if (asFile) {
        const url = URL.createObjectURL(
          new Blob([data], { type: "text/plain;charset=utf-8" })
        );
        const a = document.createElement("a");
        a.href = url;
        a.download = "TradeDesk.mq5";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("TradeDesk.mq5 downloaded");
      } else {
        await navigator.clipboard.writeText(data);
        toast.success("Full EA source copied — paste into MetaEditor");
      }
    } catch {
      toast.error("Could not fetch the EA file");
    } finally {
      setBusy(false);
    }
  };

  if (info && !info.available) return null;

  return (
    <div
      className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-4 sm:p-5"
      data-testid="ea-download-card"
    >
      <div className="flex flex-wrap items-center gap-3">
        <FileCode className="h-4 w-4 text-blue-400" />
        <div>
          <h2 className="text-base md:text-lg text-zinc-100">Expert Advisor file</h2>
          <p className="font-mono text-[11px] text-zinc-500" data-testid="ea-file-meta">
            {info
              ? `TradeDesk.mq5 · v${info.version} · ${info.lines} lines · updated ${new Date(
                  info.updated_at
                ).toLocaleString()}`
              : "loading…"}
          </p>
        </div>
        <div className="flex-1" />
        <button
          onClick={() => grab(false)}
          disabled={busy}
          data-testid="ea-file-copy"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-800 hover:border-blue-600 text-[10px] uppercase tracking-[0.1em] font-semibold text-zinc-300 hover:text-blue-400 rounded-sm disabled:opacity-50 transition-colors duration-200"
        >
          Copy source
        </button>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/ea-file`}
          onClick={(e) => {
            e.preventDefault();
            grab(true);
          }}
          data-testid="ea-file-download"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-[10px] uppercase tracking-[0.1em] font-semibold text-white rounded-sm transition-colors duration-200"
        >
          <Download className="h-3 w-3" /> Download .mq5
        </a>
      </div>
      <p className="text-[10px] text-zinc-600 font-mono mt-3 leading-relaxed">
        Always the current version. Put it in MQL5\Experts on the VPS, compile in MetaEditor
        (F7), then re-attach it to the chart. Version {info?.version || "?"} must be running for
        the latest fixes to apply.
      </p>
    </div>
  );
};
